import { useState, useContext } from "react";
import moment from "moment";
import { AuthContext } from "../../../shared/context/auth-context";
import { useHttpClient } from "../../../shared/hooks/http-hook";
import { useLoading } from "../../../shared/context/LoadingContext";
import toast from "react-hot-toast";

/* Os mesmos estilos das outras ferramentas de transferência: é o mesmo tipo de
   ecrã e deve parecer o mesmo. */
import "../transferirPagamento/TransferirPagamento.scss";
import "./TransferirIsoladosParaPlano.scss";

// Extrai um ObjectId de uma string (ID puro ou link "/clientes/<id>").
const extrairId = (valor) => {
  if (!valor) return null;
  const trimmed = valor.trim();
  const match = trimmed.match(/\/clientes\/([a-f0-9]{24})/i);
  if (match) return match[1];
  if (/^[a-f0-9]{24}$/i.test(trimmed)) return trimmed;
  return null;
};

const descreverDente = (dente, faces) =>
  [dente ? `dente ${dente}` : null, faces?.length ? `faces ${faces.join(", ")}` : null]
    .filter(Boolean)
    .join(" · ");

const descreverCandidato = (c) =>
  `Plano ${c.planoNumero ?? "?"}` +
  (c.orcamentoCodigo ? ` (orçamento ${c.orcamentoCodigo})` : "") +
  ` — ${c.designacao}` +
  (c.dente ? `, dente ${c.dente}` : "") +
  (c.nrSessoes ? ` · ${c.nrSessoes} sessão(ões)` : "") +
  (c.dentes === "parcial" ? " · PARCIAL" : "");

function TransferirIsoladosParaPlano() {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [clienteInput, setClienteInput] = useState("");
  const [isolados, setIsolados] = useState(null);

  /* Uma escolha por isolado: {incluir, procedimentoId, planoId, modo}.
     O procedimento vem pré-escolhido quando há um candidato só — é o caso
     comum — mas continua a ter de se marcar «incluir»: nada é transferido
     sem alguém o pedir. */
  const [escolhas, setEscolhas] = useState({});

  const [confirmando, setConfirmando] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const clienteId = extrairId(clienteInput);

  const alterar = (id, campos) =>
    setEscolhas((atual) => ({ ...atual, [id]: { ...atual[id], ...campos } }));

  const selecionados = (isolados || []).filter((i) => escolhas[i._id]?.incluir);

  const validacao = (() => {
    if (selecionados.length === 0) return null;

    if (selecionados.some((i) => !escolhas[i._id]?.procedimentoId)) {
      return "Escolha o procedimento de destino de cada tratamento selecionado.";
    }

    /* A mesma regra do servidor, avisada antes de tentar: um procedimento só
       recebe uma finalização, e nenhuma sessão pode ser posterior a ela. */
    const porProc = {};
    for (const i of selecionados) {
      const e = escolhas[i._id];
      (porProc[e.procedimentoId] ||= []).push({ ...e, data: new Date(i.data).getTime() });
    }
    for (const lista of Object.values(porProc)) {
      const finais = lista.filter((e) => e.modo === "finalizacao");
      if (finais.length > 1) {
        return "Um procedimento só pode receber uma finalização.";
      }
      if (
        finais.length === 1 &&
        lista.some((e) => e.modo === "sessao" && e.data > finais[0].data)
      ) {
        return "Há uma sessão com data posterior à finalização do mesmo procedimento.";
      }
    }
    return null;
  })();

  const podeExecutar = selecionados.length > 0 && !validacao && !isLoading;

  const handleCarregarDados = async () => {
    if (!clienteId) {
      toast.error("Introduza um ID ou link de cliente válido.");
      return;
    }
    setIsolados(null);
    setEscolhas({});
    startLoading();
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/manutencoes/isoladosparaplano/dados/${clienteId}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      const lista = resposta.isolados || [];
      setIsolados(lista);

      const iniciais = {};
      for (const i of lista) {
        const unico = i.candidatos.length === 1 ? i.candidatos[0] : null;
        iniciais[i._id] = {
          incluir: false,
          procedimentoId: unico?.procedimentoId || "",
          planoId: unico?.planoId || "",
          modo: "sessao",
        };
      }
      setEscolhas(iniciais);

      if (lista.length === 0) {
        toast("Este cliente não tem tratamentos isolados executados.");
      }
    } catch (err) {
      toast.error(err.message || "Erro ao carregar dados do cliente.");
    } finally {
      stopLoading();
    }
  };

  const handleExecutar = async () => {
    startLoading();
    setIsLoading(true);
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/manutencoes/isoladosparaplano`,
        "POST",
        JSON.stringify({
          clienteId,
          utilizador: auth.userId,
          itens: selecionados.map((i) => ({
            tratamentoIsoladoId: i._id,
            planoId: escolhas[i._id].planoId,
            procedimentoId: escolhas[i._id].procedimentoId,
            modo: escolhas[i._id].modo,
          })),
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      setResultado({ sucesso: true, dados: resposta });
      setConfirmando(false);
      toast.success(resposta?.message || "Transferência concluída.");
      handleCarregarDados();
    } catch (err) {
      setResultado({ sucesso: false, mensagem: err.message || "Erro desconhecido." });
      setConfirmando(false);
    } finally {
      setIsLoading(false);
      stopLoading();
    }
  };

  return (
    <div className="transferir-pagamento">
      <div className="transferir-pagamento__explicacao">
        <h3>O que faz este script?</h3>
        <p>
          Passa a <strong>execução de tratamentos isolados</strong> para o
          procedimento correspondente de um <strong>plano de tratamento</strong>{" "}
          do mesmo cliente — para quando se executou por fora um tratamento que
          já estava orçamentado.
        </p>
        <p>
          O procedimento é encontrado pelo <strong>tratamento</strong> e pelo{" "}
          <strong>dente</strong>. Para cada um escolhe-se se entra como{" "}
          <strong>sessão</strong> (o procedimento continua em aberto) ou como{" "}
          <strong>finalização</strong> (o procedimento fica concluído). A data,
          o médico, a unidade e o relatório passam para a sessão, e o material
          de stock gasto passa a pertencer-lhe.
        </p>
        <p>
          O tratamento isolado é <strong>apagado</strong> — senão a execução
          ficava contada duas vezes nas estatísticas. Uma entrada por receber
          associada a ele também é apagada, porque o orçamento do plano já cobra
          o tratamento. Tratamentos com pagamento recebido não podem ser
          transferidos: mova primeiro o pagamento com «Transferir Pagamento de
          Entrada». Se o tratamento isolado foi marcado na agenda, a marcação
          não se perde: passa a ser uma marcação atendida do plano, no mesmo dia
          e hora.
        </p>
        <div className="transferir-pagamento__aviso">
          <span>⚠</span>
          <span>
            Operação sensível sobre o registo clínico. Confirme o cliente, o
            procedimento de destino e o modo de cada tratamento antes de
            executar.
          </span>
        </div>
      </div>

      {/* 1. Cliente */}
      <div className="transferir-pagamento__campo">
        <label className="transferir-pagamento__label">Cliente</label>
        <div className="transferir-pagamento__cliente-row">
          <input
            className="transferir-pagamento__input"
            type="text"
            placeholder="ID ou link da ficha do cliente"
            value={clienteInput}
            onChange={(e) => {
              setClienteInput(e.target.value);
              setIsolados(null);
              setResultado(null);
            }}
            disabled={isLoading}
          />
          <button
            className="transferir-pagamento__btn transferir-pagamento__btn--carregar"
            onClick={handleCarregarDados}
            disabled={isLoading || !clienteId}
          >
            Carregar
          </button>
        </div>
        {clienteId && (
          <span className="transferir-pagamento__id">ID: {clienteId}</span>
        )}
      </div>

      {/* 2. Tratamentos isolados executados */}
      {isolados && isolados.length > 0 && (
        <div className="transferir-pagamento__campo">
          <label className="transferir-pagamento__label">
            Tratamentos isolados executados
            {selecionados.length > 0 && <span> — {selecionados.length} selecionado(s)</span>}
          </label>

          <div className="isolados-plano__lista">
            {isolados.map((i) => {
              const e = escolhas[i._id] || {};
              const semCandidatos = i.candidatos.length === 0;
              const bloqueado = !!i.bloqueio || semCandidatos;
              const candidato = i.candidatos.find((c) => c.procedimentoId === e.procedimentoId);

              return (
                <div
                  key={i._id}
                  className={`isolados-plano__item ${e.incluir ? "isolados-plano__item--sel" : ""} ${
                    bloqueado ? "isolados-plano__item--bloqueado" : ""
                  }`}
                >
                  <label className="isolados-plano__cabecalho">
                    <input
                      type="checkbox"
                      checked={!!e.incluir}
                      disabled={isLoading || bloqueado}
                      onChange={(ev) => alterar(i._id, { incluir: ev.target.checked })}
                    />
                    <span className="isolados-plano__nome">
                      {i.numero ? `TI ${i.numero} · ` : ""}
                      {i.designacao}
                    </span>
                    <span className="isolados-plano__meta">
                      {[
                        descreverDente(i.dente, i.faces),
                        i.data ? moment(i.data).format("DD/MM/YYYY") : null,
                        i.medico,
                        i.historicoImportado ? "importado" : null,
                        i.agenda ? "marcado na agenda" : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </label>

                  {i.bloqueio && <p className="isolados-plano__motivo">{i.bloqueio}</p>}
                  {!i.bloqueio && semCandidatos && (
                    <p className="isolados-plano__motivo">
                      Nenhum procedimento em aberto com o mesmo tratamento e dente.
                    </p>
                  )}

                  {!bloqueado && (
                    <div className="isolados-plano__destino">
                      <select
                        className="transferir-pagamento__input"
                        value={e.procedimentoId || ""}
                        disabled={isLoading}
                        onChange={(ev) => {
                          const c = i.candidatos.find((x) => x.procedimentoId === ev.target.value);
                          alterar(i._id, {
                            procedimentoId: c?.procedimentoId || "",
                            planoId: c?.planoId || "",
                          });
                        }}
                      >
                        <option value="">— procedimento de destino —</option>
                        {i.candidatos.map((c) => (
                          <option key={c.procedimentoId} value={c.procedimentoId}>
                            {descreverCandidato(c)}
                          </option>
                        ))}
                      </select>

                      <div className="isolados-plano__modo">
                        <label>
                          <input
                            type="radio"
                            name={`modo-${i._id}`}
                            checked={e.modo === "sessao"}
                            disabled={isLoading}
                            onChange={() => alterar(i._id, { modo: "sessao" })}
                          />
                          Sessão
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`modo-${i._id}`}
                            checked={e.modo === "finalizacao"}
                            disabled={isLoading}
                            onChange={() => alterar(i._id, { modo: "finalizacao" })}
                          />
                          Finalização
                        </label>
                      </div>

                      {candidato?.dentes === "parcial" && (
                        <p className="isolados-plano__aviso">
                          O procedimento abrange mais dentes ({candidato.dente}) do que este
                          tratamento ({i.dente}).
                          {e.modo === "finalizacao" &&
                            " Finalizar dá o procedimento inteiro por concluído."}
                        </p>
                      )}
                      {i.temEntradaPorReceber && (
                        <p className="isolados-plano__aviso">
                          Tem uma entrada por receber, que será apagada.
                        </p>
                      )}
                      {i.agenda && (
                        <p className="isolados-plano__aviso">
                          Foi marcado na agenda: a marcação passa para o plano, como
                          atendida.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {validacao && <p className="transferir-pagamento__erro-validacao">{validacao}</p>}

      {/* Sem nada marcado o botão fica desativado, e a validação devolve
          `null` — ficava um botão apagado sem explicação. O seletor do
          procedimento está ativo mesmo com a caixa desmarcada, e é fácil
          pensar que escolher o destino já inclui o tratamento. */}
      {isolados && isolados.length > 0 && !validacao && selecionados.length === 0 && (
        <p className="isolados-plano__dica">
          Marque a caixa de cada tratamento que quer transferir. Escolher o
          procedimento de destino não o inclui.
        </p>
      )}

      {isolados && isolados.length > 0 && (
        <>
          {!confirmando ? (
            <button
              /* `--executar` não existe na folha de estilo: o botão saía sem
                 fundo nenhum, cinzento, igual a um botão desativado. Usa o
                 mesmo das outras ferramentas de transferência. */
              className="transferir-pagamento__btn transferir-pagamento__btn--preparar"
              onClick={() => setConfirmando(true)}
              disabled={!podeExecutar}
            >
              Transferir
            </button>
          ) : (
            <div className="transferir-pagamento__confirmacao">
              <p>
                Confirma a transferência de <strong>{selecionados.length}</strong>{" "}
                tratamento(s) isolado(s)? Os tratamentos isolados serão apagados.
              </p>
              {/* `__btns` não existe na folha de estilo — sem `display: flex`
                  nem `gap`, os dois botões saíam colados. `__acoes` é a classe
                  que já lá está, usada pela ferramenta das entradas. */}
              <div className="transferir-pagamento__confirmacao__acoes">
                <button
                  className="transferir-pagamento__btn transferir-pagamento__btn--confirmar"
                  onClick={handleExecutar}
                  disabled={isLoading}
                >
                  {isLoading ? "A transferir..." : "Sim, transferir"}
                </button>
                <button
                  className="transferir-pagamento__btn transferir-pagamento__btn--cancelar"
                  onClick={() => setConfirmando(false)}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {resultado && (
        <div
          className={`transferir-pagamento__resultado ${
            resultado.sucesso
              ? "transferir-pagamento__resultado--ok"
              : "transferir-pagamento__resultado--erro"
          }`}
        >
          {resultado.sucesso ? (
            <>
              <strong>Transferência concluída.</strong>
              <p>
                Sessões: {resultado.dados.sessoes} · Finalizações:{" "}
                {resultado.dados.finalizacoes} · Registos de stock:{" "}
                {resultado.dados.consumosReapontados} · Entradas apagadas:{" "}
                {resultado.dados.entradasApagadas} · Marcações convertidas:{" "}
                {resultado.dados.marcacoesConvertidas ?? 0}
              </p>
            </>
          ) : (
            <>
              <strong>Não foi possível transferir.</strong>
              <p>{resultado.mensagem}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default TransferirIsoladosParaPlano;
